import { useState, useMemo, useCallback } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  TableSortLabel,
  Alert,
  Collapse,
  InputAdornment,
  CircularProgress,
  Button
} from "@mui/material"
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"

function AllSettlementsPage() {
  // alert('allSettlementPage')
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState("")
  const [dateFilter, setDateFilter] = useState("all")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [orderBy, setOrderBy] = useState("createdAt")
  const [order, setOrder] = useState("desc")
  const [showFilters, setShowFilters] = useState(true)

  const { data: settlements, isLoading, error, refetch } = useApiQuery(
    ["all-settlements", dateFilter],
    `/settlements?filter=${dateFilter}`
  )

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc"
      setOrder(isAsc ? "desc" : "asc")
      setOrderBy(property)
    },
    [order, orderBy]
  )

  const filteredAndSortedSettlements = useMemo(() => {
    if (!settlements) return []

    let filtered = settlements.filter((settlement) => {
      const matchesSearch =
        searchQuery === "" ||
        settlement.clientId?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        settlement.jobId?.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        settlement.settledBy?.name?.toLowerCase().includes(searchQuery.toLowerCase())

      const settlementDate = new Date(settlement.createdAt)
      const matchesDateFrom = !dateFrom || settlementDate >= new Date(dateFrom)
      const matchesDateTo = !dateTo || settlementDate <= new Date(dateTo)

      return matchesSearch && matchesDateFrom && matchesDateTo
    })

    filtered.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      if (orderBy === "createdAt") {
        aValue = new Date(aValue)
        bValue = new Date(bValue)
      } else if (orderBy === "clientId") {
        aValue = a.clientId?.name || ""
        bValue = b.clientId?.name || ""
      } else if (orderBy === "jobId") {
        aValue = a.jobId?.id || ""
        bValue = b.jobId?.id || ""
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1
      if (aValue > bValue) return order === "asc" ? 1 : -1
      return 0
    })

    return filtered
  }, [settlements, searchQuery, dateFrom, dateTo, orderBy, order])

  const paginatedSettlements = useMemo(() => {
    return filteredAndSortedSettlements.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    )
  }, [filteredAndSortedSettlements, page, rowsPerPage])

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage)
  }, [])

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSearchQuery("")
    setDateFrom("")
    setDateTo("")
    setDateFilter("all")
    setPage(0)
  }, [])

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "All Time"
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading settlements: {error.message}
      </Alert>
    )
  }

  const hasActiveFilters = searchQuery || dateFrom || dateTo || dateFilter !== "all"

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Settlements
          </Typography>
          <Typography variant="body2" color="textSecondary">
            View and manage all fee settlements
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)} color="primary">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Stats Summary */}
      {/* <Stack direction="row" spacing={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="primary">
            {settlements?.length || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Settlements
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="success.main">
            {filteredAndSortedSettlements.length}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Filtered Results
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="info.main">
            ₹
            {filteredAndSortedSettlements
              .reduce((sum, s) => sum + (s.totalUsed || 0), 0)
              .toLocaleString()}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Settled Amount
          </Typography>
        </Paper>
      </Stack> */}

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search by client name, job ID, or settler name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(0)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter Row */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={dateFilter}
                  label="Time Period"
                  onChange={(e) => {
                    setDateFilter(e.target.value)
                    setPage(0)
                  }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                </Select>
              </FormControl>

              <TextField
                type="date"
                label="Date From"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(0)
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />

              <TextField
                type="date"
                label="Date To"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(0)
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />

              {hasActiveFilters && (
                <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Collapse>

      {/* Settlements Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "createdAt"}
                    direction={orderBy === "createdAt" ? order : "asc"}
                    onClick={() => handleRequestSort("createdAt")}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "clientId"}
                    direction={orderBy === "clientId" ? order : "asc"}
                    onClick={() => handleRequestSort("clientId")}
                  >
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "jobId"}
                    direction={orderBy === "jobId" ? order : "asc"}
                    onClick={() => handleRequestSort("jobId")}
                  >
                    Job ID
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "totalUsed"}
                    direction={orderBy === "totalUsed" ? order : "asc"}
                    onClick={() => handleRequestSort("totalUsed")}
                  >
                    Amount Used
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "balanceCarried"}
                    direction={orderBy === "balanceCarried" ? order : "asc"}
                    onClick={() => handleRequestSort("balanceCarried")}
                  >
                    Balance Carried
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell>Settled By</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSettlements.map((settlement) => (
                <TableRow key={settlement._id} hover sx={{ cursor: "pointer" }}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">
                          {new Date(settlement.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(settlement.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {settlement.clientId?.name || "N/A"}
                    </Typography>
                    {settlement.clientId?.phoneNumber && (
                      <Typography variant="caption" color="textSecondary">
                        {settlement.clientId.phoneNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={settlement.jobId || "N/A"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ₹{(settlement.totalUsed || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      ₹{(settlement.balanceCarried || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  {/* <TableCell>
                    <Typography variant="body2">{settlement.settledBy?.name || "N/A"}</Typography>
                  </TableCell> */}
                </TableRow>
              ))}
              {paginatedSettlements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No settlements found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {hasActiveFilters
                        ? "Try adjusting your filters to see more results."
                        : "No settlements available at the moment."}
                    </Typography>
                    {hasActiveFilters && (
                      <Button variant="outlined" onClick={handleClearFilters} sx={{ mt: 2 }}>
                        Clear All Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredAndSortedSettlements.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>
    </Box>
  )
}

export default AllSettlementsPage